// ---------------------------------------------------------------------------
// Render-time token + layout resolution (§6.1, §6.2).
//
// CANONICAL COPY. The resolver is duplicated only into the Deno exporter
// (supabase/functions/google-workspace/lib/theming/resolve.ts) — the backend
// never resolves (§6.3e). The Deno copy has NO DEFAULT_THEME, so its valid-token
// fallback (case b below) is unreachable there; otherwise keep the bodies in sync.
//
// `theme` is always required: callers pass `presentation.theme ?? DEFAULT_THEME`,
// so legacy/un-themed decks (raw values only) resolve harmlessly.
// ---------------------------------------------------------------------------

import type {
  ColorToken,
  ElementPosition,
  FontToken,
  ImageElement,
  InteractiveElement,
  PlaceholderDefinition,
  ResolvedTheme,
  ShapeElement,
  Slide,
  SlideElement,
  SlideTemplate,
  SlideTextStyle,
  TextElement,
  VideoElement,
} from '../types/slide';

import { DEFAULT_THEME } from './default-theme';
import { isColorToken, isFontToken } from './tokens';

type WithResolvedGeometry<E extends SlideElement> = E & {
  /** Never absent post-resolution. */
  position: ElementPosition;
  /** Present only on materialized decoration. */
  source?: 'theme';
};

/** Render-ready element: a guaranteed concrete `position`, and — for text — a
 *  guaranteed resolved `style` (every token → hex/family, placeholder defaults
 *  merged in per-field). Consumers render this verbatim and never re-resolve. */
export type ResolvedSlideElement =
  | (WithResolvedGeometry<TextElement> & { style: SlideTextStyle })
  | WithResolvedGeometry<ShapeElement>
  | WithResolvedGeometry<ImageElement>
  | WithResolvedGeometry<InteractiveElement>
  | WithResolvedGeometry<VideoElement>;

/** A slide projected to a fully-resolved, decoration-free view — concrete
 *  elements + background. The accessibility checker (§6.3c) consumes this so its
 *  logic is unchanged while reading concrete fields. */
export interface ResolvedSlide extends Omit<Slide, 'elements' | 'background'> {
  elements: ResolvedSlideElement[];
  background: { color: string };
}

/** Editor hard-default geometry for a free element with no stored position.
 *  Mirrors the normalizer's `normalizePosition` default (80,80,240,80). */
const HARD_DEFAULT_POSITION: ElementPosition = {
  x: 80,
  y: 80,
  width: 240,
  height: 80,
  rotation: 0,
  zIndex: 1,
};

const warnedTokens = new Set<string>();
function warnUnknownToken(value: string): void {
  if (warnedTokens.has(value)) return;
  warnedTokens.add(value);
  // Unreachable for well-formed stored content — malformed tokens are dropped at
  // the author/normalize boundary (§7.3). Defensive only; never throws.
  console.warn(`[theming] unknown token "${value}" — falling back to field default`);
}

// ─── Single-value resolution (§6.1) ────────────────────────────────────────

export function resolveColor(value: string | undefined, theme: ResolvedTheme): string | undefined {
  if (value === undefined) return undefined;
  if (!value.startsWith('$')) return value; // raw — passed through unchanged
  if (isColorToken(value)) {
    // Safe: isColorToken validated that the name is a ColorToken.
    const name = value.slice(1) as ColorToken;
    return theme.palette[name] ?? DEFAULT_THEME.palette[name];
  }
  warnUnknownToken(value); // $-prefixed but invalid name → field default
  return undefined;
}

export function resolveFont(value: string | undefined, theme: ResolvedTheme): string | undefined {
  if (value === undefined) return undefined;
  if (!value.startsWith('$')) return value;
  if (isFontToken(value)) {
    // Safe: isFontToken validated that the name is a FontToken.
    const name = value.slice(1) as FontToken;
    return theme.fonts[name] ?? DEFAULT_THEME.fonts[name];
  }
  warnUnknownToken(value);
  return undefined;
}

/** Resolves the token-bearing fields of a text style in place on a fresh copy.
 *  A token that resolves to `undefined` (invalid name) is dropped so the
 *  consumer's hard default applies rather than a literal token leaking. */
export function resolveTextStyle(style: SlideTextStyle, theme: ResolvedTheme): SlideTextStyle {
  const out: SlideTextStyle = { ...style };
  setOrDelete(out, 'fontFamily', resolveFont(style.fontFamily, theme));
  setOrDelete(out, 'color', resolveColor(style.color, theme));
  setOrDelete(out, 'backgroundColor', resolveColor(style.backgroundColor, theme));
  setOrDelete(out, 'borderColor', resolveColor(style.borderColor, theme));
  return out;
}

function setOrDelete<T, K extends keyof T>(obj: T, key: K, value: T[K] | undefined): void {
  if (value === undefined) delete obj[key];
  else obj[key] = value;
}

// ─── Element / slide resolution (§6.2) ─────────────────────────────────────

function layoutFor(slide: Slide, theme: ResolvedTheme) {
  const template: SlideTemplate = slide.template ?? 'BLANK';
  return theme.layouts[template] ?? theme.layouts.BLANK;
}

function mergePosition(
  override: Partial<ElementPosition> | undefined,
  base: ElementPosition | undefined
): ElementPosition {
  const b = base ?? HARD_DEFAULT_POSITION;
  return {
    x: override?.x ?? b.x,
    y: override?.y ?? b.y,
    width: override?.width ?? b.width,
    height: override?.height ?? b.height,
    rotation: override?.rotation ?? b.rotation,
    ...((override?.flipH ?? b.flipH) ? { flipH: true } : {}),
    ...((override?.flipV ?? b.flipV) ? { flipV: true } : {}),
    zIndex: override?.zIndex ?? b.zIndex,
  };
}

/**
 * Resolve one stored element into a render-ready element WITHOUT mutating it:
 * inherit placeholder geometry/style (per-field, element wins), fill missing
 * geometry from the editor hard-default, and resolve every token. Used by both
 * `buildRenderElements` and the editor's resolved↔stored bridge.
 */
export function resolveStoredElement(
  stored: SlideElement,
  slide: Slide,
  theme: ResolvedTheme
): ResolvedSlideElement {
  const placeholder: PlaceholderDefinition | undefined =
    stored.type === 'text' && stored.placeholderId !== undefined
      ? layoutFor(slide, theme).placeholders.find((p) => p.placeholderId === stored.placeholderId)
      : undefined;

  const position = mergePosition(stored.position, placeholder?.position);

  if (stored.type === 'text') {
    // Per-field precedence: element style over placeholder default; then resolve.
    const merged: SlideTextStyle = { ...(placeholder?.textStyle ?? {}), ...(stored.style ?? {}) };
    return { ...stored, position, style: resolveTextStyle(merged, theme) };
  }

  if (stored.type === 'shape') {
    const content = { ...stored.content };
    setOrDelete(content, 'fillColor', resolveColor(content.fillColor, theme));
    setOrDelete(content, 'strokeColor', resolveColor(content.strokeColor, theme));
    return { ...stored, position, content };
  }

  // image / interactive / video carry no token-bearing fields — only geometry.
  return { ...stored, position };
}

function materializeDecoration(
  deco: { id?: string },
  slide: Slide,
  theme: ResolvedTheme,
  index: number
): ResolvedSlideElement {
  // A DecorationElement is structurally a SlideElement minus a required `id`;
  // assigning one gives a full SlideElement we can resolve like any other.
  // The fallback id is namespaced by slide.id: decoration is materialized per
  // slide from one shared layout, so a bare `theme-deco-${index}` repeats across
  // slides and collides as a Google Slides objectId (which must be unique across
  // all pages/elements) at export.
  const element = { ...deco, id: deco.id ?? `theme-deco-${slide.id}-${index}` } as SlideElement;
  return { ...resolveStoredElement(element, slide, theme), source: 'theme' };
}

/**
 * Full per-render expansion for one slide (§6.2): materialized decoration +
 * placeholder-bound elements (geometry/style inherited) + free elements, all
 * tokens resolved, ordered by `zIndex` (ties → decoration behind, then stable).
 */
export function buildRenderElements(slide: Slide, theme: ResolvedTheme): ResolvedSlideElement[] {
  const layout = layoutFor(slide, theme);
  const out: ResolvedSlideElement[] = [];

  // 1. Decoration (Phase 1 DEFAULT theme: always []).
  layout.decoration.forEach((deco, i) => out.push(materializeDecoration(deco, slide, theme, i)));

  // 2 + 3. Placeholder-bound and free user elements.
  for (const element of slide.elements ?? []) {
    out.push(resolveStoredElement(element, slide, theme));
  }

  // 4. Order by zIndex; ties put decoration first (behind), else keep input order.
  return out
    .map((element, index) => ({ element, index }))
    .sort((a, b) => {
      if (a.element.position.zIndex !== b.element.position.zIndex) {
        return a.element.position.zIndex - b.element.position.zIndex;
      }
      const aDeco = a.element.source === 'theme' ? 0 : 1;
      const bDeco = b.element.source === 'theme' ? 0 : 1;
      return aDeco !== bDeco ? aDeco - bDeco : a.index - b.index;
    })
    .map(({ element }) => element);
}

/** Effective slide background (§6.2): slide override → layout background →
 *  white, with any token resolved. */
export function resolveSlideBackground(slide: Slide, theme: ResolvedTheme): string {
  const raw = slide.background?.color ?? layoutFor(slide, theme).background;
  return resolveColor(raw, theme) ?? '#ffffff';
}

/**
 * A slide projected into a fully-resolved, decoration-free view for the
 * accessibility checker (§6.3c / §8.4): user elements get concrete geometry +
 * resolved style, the background is concrete. The checker is unmodified and
 * consumes this instead of the raw stored slide.
 */
export function resolveSlideForChecker(slide: Slide, theme: ResolvedTheme): ResolvedSlide {
  return {
    ...slide,
    elements: buildRenderElements(slide, theme).filter((element) => element.source !== 'theme'),
    background: { color: resolveSlideBackground(slide, theme) },
  };
}
